import { isInlineActive, wrapInline } from '../InlinePlugin/utils';
import { Transforms, type Editor, type Element } from 'slate';
import {
  type LinkCardElement,
  type LinkCardAlignment,
  type LinkCardOrientation,
} from '../../types/aha-editor';
import { DEFAULT_WIDTH } from './constants';
import { isLinkCardElement } from '../../types/guards';
import { ReactEditor } from 'slate-react';
import LinkCardCommon from './LinkCardCommon';

export function updateLinkCardProperty(
  editor: Editor | undefined,
  element: Element | undefined,
  property: string,
  value: any
) {
  if (!editor || !element || !isLinkCardElement(element)) return;
  const path = ReactEditor.findPath(editor, element);
  if (!path) return;
  Transforms.setNodes(
    editor,
    {
      [property]: value,
    },
    {
      at: path,
    }
  );

  // Update localStorage preferences for specific properties
  switch (property) {
    case 'linkCard_alignment':
      localStorage.setItem(LinkCardCommon.linkCardAlignmentPrefKey, value);
      break;
    case 'linkCard_orientation':
      localStorage.setItem(LinkCardCommon.linkCardOrientationPrefKey, value);
      break;
    case 'linkCard_fullWidth':
      localStorage.setItem(
        LinkCardCommon.linkCardFullWidthPrefKey,
        String(value)
      );
      break;
    case 'linkCard_keepRatio':
      localStorage.setItem(
        LinkCardCommon.linkCardKeepRatioPrefKey,
        String(value)
      );
      break;
  }
}

// A LinkCard is considered to have an image if the linkCard_image property is set to a non-null, non-empty string.
// If linkCard_image is undefined, it means the user has never set an image, so we treat it as having a placeholder image.
// If linkCard_image is an empty string, it means the user has explicitly removed the image, so we treat it as not having an image.
// If linkCard_image is any other non-empty string, it means the user has set a custom image URL, so we treat it as having an image.
export const hasImageOrPlaceholder = (image: string | undefined | null) => {
  return image !== '';
};

export const isLinkCardActive = (editor: Editor) => {
  return isInlineActive(editor, 'linkCard');
};

export const wrapLinkCard = (editor: Editor, text: string, isUrl: boolean) => {
  if (isLinkCardActive(editor)) {
    return;
  }

  const buildElement = (): LinkCardElement => {
    // Get the stored alignment preference, default to 'left' if not found
    const alignmentPreference = localStorage.getItem(
      LinkCardCommon.linkCardAlignmentPrefKey
    ) as LinkCardAlignment | null;
    const defaultAlignment: LinkCardAlignment =
      alignmentPreference &&
      ['left', 'center', 'right'].includes(alignmentPreference)
        ? alignmentPreference
        : 'left';

    // Get the stored orientation preference, default to 'vertical' if not found
    const orientationPreference = localStorage.getItem(
      LinkCardCommon.linkCardOrientationPrefKey
    ) as LinkCardOrientation | null;
    const defaultOrientation: LinkCardOrientation =
      orientationPreference &&
      ['vertical', 'horizontal'].includes(orientationPreference)
        ? orientationPreference
        : 'vertical';

    // Get the stored fullWidth preference, default to false if not found
    const fullWidthPreference = localStorage.getItem(
      LinkCardCommon.linkCardFullWidthPrefKey
    );
    const defaultFullWidth = fullWidthPreference === 'true';

    // Get the stored keepRatio preference, default to true if not found
    const keepRatioPreference = localStorage.getItem(
      LinkCardCommon.linkCardKeepRatioPrefKey
    );
    const defaultKeepRatio = keepRatioPreference !== 'false';

    return {
      type: 'linkCard',
      linkCard_title: text ? (isUrl ? 'Title' : text) : 'Title',
      linkCard_description: 'Description',
      linkCard_href: isUrl ? text : '',
      linkCard_linkText: isUrl ? text : '',
      linkCard_image: undefined,
      linkCard_keepRatio: defaultKeepRatio,
      linkCard_fullWidth: defaultFullWidth,
      linkCard_orientation: defaultOrientation,
      linkCard_alignment: defaultAlignment,
      resizer_width: DEFAULT_WIDTH,
      resizer_height: undefined,
      children: [{ text: '' }],
    };
  };

  wrapInline(editor, buildElement);
};
